import React, { useEffect, useState } from 'react';
import { Modal, Form, Input, Button, Table, message, Typography, Space, Alert, Tag, Input as AntInput } from 'antd';
import axios from 'axios';
import { API_URL } from '../config';

const { Text, Paragraph } = Typography;
const { Search } = AntInput;

interface CreateRoomFeatureModalProps {
  visible: boolean;
  hotelId: number;
  onClose: () => void;
  onSuccess: () => void;
}

interface RoomRow {
  id: number;
  name: string;
  floorNumber: string | null;
  roomType?: string;
}

const CreateRoomFeatureModal: React.FC<CreateRoomFeatureModalProps> = ({
  visible,
  hotelId,
  onClose,
  onSuccess,
}) => {
  const [form] = Form.useForm();
  const [submitting, setSubmitting] = useState(false);
  const [rooms, setRooms] = useState<RoomRow[]>([]);
  const [loadingRooms, setLoadingRooms] = useState(false);
  const [selectedRoomIds, setSelectedRoomIds] = useState<number[]>([]);
  const [roomSearch, setRoomSearch] = useState('');

  useEffect(() => {
    if (visible && hotelId !== undefined) {
      loadRooms();
    } else if (!visible) {
      form.resetFields();
      setSelectedRoomIds([]);
      setRoomSearch('');
      setRooms([]);
    }
  }, [visible, hotelId]);

  const loadRooms = async () => {
    setLoadingRooms(true);
    try {
      const response = await axios.get(`${API_URL}/hotels/${hotelId}/rooms`);
      const data: any[] = response.data;

      const uniqueMap = new Map<number, RoomRow>();
      for (const item of data) {
        const id = item.id;
        if (!id || uniqueMap.has(id)) continue;
        uniqueMap.set(id, {
          id,
          name: item.room || `Room ${id}`,
          floorNumber: item.floor || null,
          roomType: item.roomType && item.roomType !== '-' ? item.roomType : undefined,
        });
      }
      setRooms(
        Array.from(uniqueMap.values()).sort((a, b) => {
          const aNum = parseInt(a.name, 10);
          const bNum = parseInt(b.name, 10);
          if (!isNaN(aNum) && !isNaN(bNum)) return aNum - bNum;
          return a.name.localeCompare(b.name);
        }),
      );
    } catch (error) {
      console.error('Failed to load rooms:', error);
      message.error('Failed to load rooms');
    } finally {
      setLoadingRooms(false);
    }
  };

  const handleSubmit = async (values: any) => {
    const name = (values.name || '').trim();
    if (!name) {
      message.warning('Please enter a feature name');
      return;
    }

    setSubmitting(true);
    try {
      const createResp = await axios.post(`${API_URL}/room-features`, {
        hotelId,
        name,
        classification: values.classification?.trim() || undefined,
      });
      const newFeature = createResp.data;

      if (selectedRoomIds.length > 0) {
        const assignResp = await axios.post(`${API_URL}/rooms/bulk-assign-feature`, {
          hotelId,
          featureMewsId: newFeature.mewsId,
          roomIds: selectedRoomIds,
        });
        const { updated, alreadyAssigned, notFound } = assignResp.data;

        const parts = [`Feature "${name}" created`, `assigned to ${updated} room(s)`];
        if (alreadyAssigned > 0) parts.push(`${alreadyAssigned} already had it`);
        if (notFound > 0) parts.push(`${notFound} not found`);
        message.success(parts.join(', '));
      } else {
        message.success(`Feature "${name}" created`);
      }

      onSuccess();
      onClose();
    } catch (error: any) {
      const msg = error.response?.data?.message || error.message || 'Failed to create feature';
      message.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const filteredRooms = roomSearch.trim()
    ? rooms.filter((r) =>
        r.name.toLowerCase().includes(roomSearch.toLowerCase()) ||
        (r.floorNumber || '').toLowerCase().includes(roomSearch.toLowerCase()) ||
        (r.roomType || '').toLowerCase().includes(roomSearch.toLowerCase()),
      )
    : rooms;

  const columns = [
    {
      title: 'Room',
      dataIndex: 'name',
      key: 'name',
      width: 140,
    },
    {
      title: 'Floor',
      dataIndex: 'floorNumber',
      key: 'floorNumber',
      render: (val: string | null) =>
        val ? <Tag color="blue">{val}</Tag> : <Text type="secondary">—</Text>,
    },
    {
      title: 'Room Type',
      dataIndex: 'roomType',
      key: 'roomType',
      render: (val?: string) => val || <Text type="secondary">—</Text>,
    },
  ];

  return (
    <Modal
      title="Create Room Feature"
      open={visible}
      onCancel={onClose}
      width={800}
      footer={[
        <Button key="cancel" onClick={onClose}>
          Cancel
        </Button>,
        <Button
          key="submit"
          type="primary"
          loading={submitting}
          onClick={() => form.submit()}
        >
          Create Feature
          {selectedRoomIds.length > 0 ? ` & Assign to ${selectedRoomIds.length} Room(s)` : ''}
        </Button>,
      ]}
    >
      <Form form={form} layout="vertical" onFinish={handleSubmit}>
        <Form.Item
          label="Feature Name (English)"
          name="name"
          rules={[{ required: true, message: 'Please enter a feature name' }]}
          extra="Shown as the feature's English name. Other languages can be added later from the feature's edit view."
        >
          <Input placeholder="e.g. Sea View, WiFi, Balcony" />
        </Form.Item>

        <Form.Item
          label="Classification (optional)"
          name="classification"
          extra="Optional category/tag (e.g. View, Amenity). Free-text."
        >
          <Input placeholder="e.g. Amenity" />
        </Form.Item>

        <Paragraph strong style={{ marginTop: 12, marginBottom: 8 }}>
          Assign Feature to Rooms (optional)
        </Paragraph>

        <Space direction="vertical" size="small" style={{ width: '100%', marginBottom: 8 }}>
          {selectedRoomIds.length > 0 && (
            <Alert
              type="info"
              showIcon
              message={`${selectedRoomIds.length} room(s) selected`}
              description="Selected rooms will have this feature added to their assigned features list."
            />
          )}
          <Search
            placeholder="Search rooms..."
            allowClear
            value={roomSearch}
            onChange={(e) => setRoomSearch(e.target.value)}
            style={{ width: 300 }}
          />
        </Space>

        <Table
          rowKey="id"
          size="small"
          loading={loadingRooms}
          dataSource={filteredRooms}
          columns={columns}
          pagination={{ pageSize: 10, showSizeChanger: false }}
          scroll={{ y: 300 }}
          rowSelection={{
            selectedRowKeys: selectedRoomIds,
            onChange: (keys) => setSelectedRoomIds(keys as number[]),
            preserveSelectedRowKeys: true,
          }}
        />
      </Form>
    </Modal>
  );
};

export default CreateRoomFeatureModal;
